import React from 'react';
import Grid from '@mui/material/Grid2';
import { Box, IconButton, Link, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLanguage } from 'Context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import FielderLogoImage from '@assets/icons/logo-login-icon.svg';
import FielderLogoNegative from '@assets/icons/logo-login-icon-light.svg';
import { ENDPOINTS } from '../../../core/constants/pageEndpoints.constants';
import { useSmartTheme } from '@core/theme/SmartThemeProvider';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
const PRIVACY_POLICY_LINK = 'https://www.appfielder.com/policy';
const TERMS_OF_SERVICE_LINK = 'https://www.appfielder.com/terms-of-service/';
const MotionLoginPaper = motion(Paper);
const LoginCard = ({ children, show }) => {
  const lan = useLanguage();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { mode, toggleMode } = useSmartTheme();
  const logoSrc = isDarkMode ? FielderLogoNegative : FielderLogoImage;
  const handleForgotPassword = () => navigate(ENDPOINTS.recoverPassword);
  const handleCognitoProvider = () => navigate(ENDPOINTS.cognitoProvider);
  const openPage = (page) => () => {
    let link;
    if (page === 'termsOfService') {
      link = TERMS_OF_SERVICE_LINK;
    } else if (page === 'privacyPolicy') {
      link = PRIVACY_POLICY_LINK;
    } else return;
    window.open(link, '_blank');
  };

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <IconButton
        onClick={toggleMode}
        aria-label="toggle theme"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 5,
          p: 0.5,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <EmojiObjectsIcon
          fontSize="medium"
          sx={{
            color: isDarkMode ? '#858f9b' : '#FFC95C',
            transition: 'color 0.2s ease',
            '&:hover': {
              color: isDarkMode ? '#FFC95C' : '#858f9b',
            },
          }}
        />
      </IconButton>
      <AnimatePresence>
        {show && (
          <motion.div initial={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
            <MotionLoginPaper elevation={1} sx={{ pt: 4, pb: 4, px: 1 }}>
              <Grid
                container
                spacing={4}
                wrap="nowrap"
                direction="column"
                justifyContent="center"
                alignItems="stretch"
                sx={{ textAlign: 'center' }}
              >
                {/* Logo */}
                <Grid size={12} sx={{ display: 'flex', justifyContent: 'center', mb: 8 }}>
                  <Box
                    component="img"
                    src={logoSrc}
                    alt="Fielder logo"
                    sx={{
                      width: '35%',
                      maxWidth: 280,
                      height: 'auto',
                      display: 'block',
                      marginInline: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                </Grid>
                {/* Form fields / children */}
                {children}
                {/* Forgot password / Provider buttons */}
                <Grid
                  container
                  size={12}
                  justifyContent="center"
                  sx={{ mt: 1 }}
                >
                  <Grid size={{ xs: 12, sm: 8 }} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, }}>
                    <Link
                      gutterBottom sx={{ display: 'block' }}
                      component="button"
                      variant="body2"
                      onClick={handleForgotPassword}>
                      {lan.forgotPass}
                    </Link>
                    <Link
                      gutterBottom sx={{ display: 'block' }}
                      component="button"
                      variant="body2"
                      onClick={handleCognitoProvider}>
                      {lan.selectSignInProvider}
                    </Link>
                  </Grid>
                </Grid>
                {/* Terms & Privacy */}
                <Grid container size={12} justifyContent="center" sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, sm: 10 }}>
                    <Typography
                      variant={'caption'} gutterBottom
                      sx={{ display: 'block', color: '#858f9b' }}>
                      {lan.termsAndConditionsLogin}
                      <Link
                        underline="hover"
                        component="button"
                        variant="caption"
                        onClick={openPage('termsOfService')}>
                        {lan.termsAndConditions2}
                      </Link>
                      {lan.and}
                      <Link
                        underline="hover"
                        component="button"
                        variant="caption"
                        onClick={openPage('privacyPolicy')}>
                        {lan.privacyPolicy}
                      </Link>
                    </Typography>
                  </Grid>
                </Grid>
                {/* Version / Footer */}
                <Grid size={12} sx={{ mt: 1, display: 'flex', justifyContent: 'center', }}>
                  <Typography
                    variant={'caption'} gutterBottom
                    sx={{
                      display: 'block', color: '#858f9b'
                  }}
                  >
                    {`© ${process.env.FIELDER_APP_YEAR} v${process.env.FIELDER_APP_VERSION}`}
                  </Typography>
                </Grid>
              </Grid>
            </MotionLoginPaper>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};
export default LoginCard;
//Original code:
/*import React from 'react';
import FielderLogoImage from '@assets/icons/logo-login-icon.svg';
import Grid from '@mui/material/Grid2';
import { useLanguage } from 'Context/LanguageContext';
import UnderlinedButton from 'FielderComponents/UnderlinedButton';
import { useNavigate } from 'react-router-dom';
import {motion, AnimatePresence} from 'framer-motion'
import { ENDPOINTS } from '../../../core/constants/pageEndpoints.constants';
import { FielderLogo, TermsAndServiceButton, LoginSecondaryText, LoginPaper } from '../LoginPage.styles';

const PRIVACY_POLICY_LINK = 'https://www.appfielder.com/policy';
const TERMS_OF_SERVICE_LINK = 'https://www.appfielder.com/terms-of-service/';
const MotionLoginPaper = motion(LoginPaper);
const x = motion(LoginCard)
const LoginCard = ({ children, show }) => {
	const lan = useLanguage();
	const navigate = useNavigate();
	const handleForgotPassword = () => navigate(ENDPOINTS.recoverPassword);
  const handleCognitoProvider = () => navigate(ENDPOINTS.cognitoProvider);
	const openPage = (page) => () => {
		let link;
		if (page === 'termsOfService') {
			link = TERMS_OF_SERVICE_LINK;
		} else if (page === 'privacyPolicy') {
			link = PRIVACY_POLICY_LINK;
		} else return;
		window.open(link, '_blank');
	};
	return (
		<div style={{ flexGrow: 1 }}>
			<AnimatePresence>
				{show &&
					<motion.div initial={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
					<MotionLoginPaper
					>
						<Grid
							container
							spacing={4}
							wrap='nowrap'
							direction='column'
							justifyContent='center'
							alignItems='stretch'
							sx={{textAlign: 'center'}}
						>
							<Grid size={12} sx={{ pb: 4 }}>
                <FielderLogo src={FielderLogoImage} alt=''/>
              </Grid>
							{children}
              <Grid size={8} className='mx-auto' justifyContent="space-between">
                <UnderlinedButton onClick={handleForgotPassword}>
                  {lan.forgotPass}
                </UnderlinedButton>
                <UnderlinedButton onClick={handleCognitoProvider}>
                  {lan.selectSignInProvider}
                </UnderlinedButton>
              </Grid>
							<Grid size={10} className='mx-auto'>
								<LoginSecondaryText>
									{lan.termsAndConditionsLogin}
									<TermsAndServiceButton onClick={openPage('termsOfService')}>
										{lan.termsAndConditions2}
									</TermsAndServiceButton>
									{lan.and}
									<TermsAndServiceButton onClick={openPage('privacyPolicy')}>{lan.privacyPolicy}</TermsAndServiceButton>
								</LoginSecondaryText>
							</Grid>
							<Grid>
								<LoginSecondaryText>
									{`© ${process.env.FIELDER_APP_YEAR} v${process.env.FIELDER_APP_VERSION}`}
								</LoginSecondaryText>
							</Grid>
						</Grid>
						</MotionLoginPaper>
						</motion.div>
				}
			</AnimatePresence>
		</div>
	);
};
export default LoginCard;
*/
